/**
 * Export Dialog Handler
 * Provides UI for file export dialog with format selection
 */

import { EXPORT_FORMATS, EXPORT_CONFIG, generateFileName, getFormatConfig } from '../exportConfig.js';
import { downloadMesh } from './modelExporter.js';

/**
 * Show export dialog and handle user selection
 * This uses the browser's native file save dialog when available
 * Falls back to automatic download with format selector
 *
 * @param {THREE.Mesh} mesh - The mesh to export
 * @param {Object} options - Dialog options
 *   - defaultFormat: 'stl' or 'ply' (default: 'stl')
 *   - defaultFilename: base filename (default: 'socket')
 * @returns {Promise<void>}
 */
export async function showExportDialog(mesh, options = {}) {
  const {
    defaultFormat = EXPORT_CONFIG.defaultFormat,
    defaultFilename = EXPORT_CONFIG.defaultFileName
  } = options;

  // Try using modern file system access API
  if (window.showSaveFilePicker && typeof window.showSaveFilePicker === 'function') {
    await exportWithFilePicker(mesh, defaultFormat, defaultFilename);
  } else {
    // Fallback to format selection dialog
    await exportWithFormatDialog(mesh, defaultFormat, defaultFilename);
  }
}

/**
 * Export using the File System Access API (if available in browser)
 * @param {THREE.Mesh} mesh - The mesh to export
 * @param {string} format - Default format
 * @param {string} filename - Base filename
 */
async function exportWithFilePicker(mesh, format, filename) {
  try {
    const formatConfig = getFormatConfig(format);
    const fullFilename = generateFileName(filename, format);

    const handle = await window.showSaveFilePicker({
      suggestedName: fullFilename,
      types: [
        {
          description: formatConfig.label,
          accept: {
            [formatConfig.mimeType]: [formatConfig.extension]
          }
        }
      ]
    });

    const writable = await handle.createWritable();
    const { downloadMesh } = await import('./modelExporter.js');

    // We still use downloadMesh but this requires modification
    // For now, use the blob approach
    const { exportMesh } = await import('./modelExporter.js');
    const blob = exportMesh(mesh, { format });

    await writable.write(blob);
    await writable.close();
  } catch (error) {
    // User cancelled or error occurred
    if (error.name !== 'AbortError') {
      console.error('File picker error:', error);
    }
  }
}

/**
 * Export with format selection dialog (fallback method)
 * @param {THREE.Mesh} mesh - The mesh to export
 * @param {string} defaultFormat - Default format
 * @param {string} defaultFilename - Base filename
 */
async function exportWithFormatDialog(mesh, defaultFormat, defaultFilename) {
  return new Promise((resolve) => {
    // Create dialog element
    const dialog = createFormatDialog(
      mesh,
      defaultFormat,
      defaultFilename,
      resolve
    );

    document.body.appendChild(dialog);

    // Show dialog
    const dialogElement = dialog.querySelector('[role="dialog"]');
    if (dialogElement && dialogElement.showModal) {
      dialogElement.showModal();
    }
  });
}

/**
 * Create format selection dialog
 * @param {THREE.Mesh} mesh - The mesh to export
 * @param {string} defaultFormat - Default format
 * @param {string} defaultFilename - Base filename
 * @param {Function} onComplete - Completion callback
 * @returns {HTMLElement} Dialog element
 */
function createFormatDialog(mesh, defaultFormat, defaultFilename, onComplete) {
  const container = document.createElement('div');
  container.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
  `;

  const dialog = document.createElement('dialog');
  dialog.setAttribute('role', 'dialog');
  dialog.style.cssText = `
    border: none;
    border-radius: 12px;
    padding: 32px;
    max-width: 500px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  `;

  // Dialog content
  const title = document.createElement('h2');
  title.textContent = 'Export Socket';
  title.style.cssText = 'margin: 0 0 16px 0; font-size: 20px; color: #333;';

  const description = document.createElement('p');
  description.textContent = 'Select export format and filename:';
  description.style.cssText = 'margin: 0 0 24px 0; color: #666; font-size: 14px;';

  // Filename input
  const filenameLabel = document.createElement('label');
  filenameLabel.textContent = 'Filename:';
  filenameLabel.style.cssText = 'display: block; margin-bottom: 8px; font-weight: 500; color: #333;';

  const filenameInput = document.createElement('input');
  filenameInput.type = 'text';
  filenameInput.value = defaultFilename;
  filenameInput.style.cssText = `
    width: 100%;
    padding: 10px;
    margin-bottom: 24px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 14px;
    box-sizing: border-box;
  `;

  // Format selection
  const formatLabel = document.createElement('label');
  formatLabel.textContent = 'Format:';
  formatLabel.style.cssText = 'display: block; margin-bottom: 12px; font-weight: 500; color: #333;';

  const formatGroup = document.createElement('div');
  formatGroup.style.cssText = 'margin-bottom: 24px;';

  // Create radio buttons for each format
  Object.entries(EXPORT_CONFIG.formats).forEach(([formatKey, formatConfig]) => {
    const radioContainer = document.createElement('div');
    radioContainer.style.cssText = 'margin-bottom: 12px;';

    const radio = document.createElement('input');
    radio.type = 'radio';
    radio.name = 'export-format';
    radio.value = formatKey;
    radio.checked = formatKey === defaultFormat;
    radio.id = `format-${formatKey}`;
    radio.style.cssText = 'margin-right: 8px; cursor: pointer;';

    const label = document.createElement('label');
    label.htmlFor = `format-${formatKey}`;
    label.style.cssText = 'cursor: pointer; user-select: none;';

    const labelText = document.createElement('span');
    labelText.textContent = `${formatConfig.label}`;
    labelText.style.cssText = 'font-weight: 500; display: block;';

    const description = document.createElement('span');
    description.textContent = formatConfig.description;
    description.style.cssText = 'font-size: 13px; color: #666; display: block; margin-left: 24px; margin-top: 4px;';

    if (formatConfig.preservesColor) {
      const colorNote = document.createElement('span');
      colorNote.textContent = '✓ Preserves colors';
      colorNote.style.cssText = 'font-size: 13px; color: #10B981; margin-left: 24px; display: block;';
      description.appendChild(document.createElement('br'));
      description.appendChild(colorNote);
    }

    label.appendChild(labelText);
    label.appendChild(description);
    radioContainer.appendChild(radio);
    radioContainer.appendChild(label);
    formatGroup.appendChild(radioContainer);
  });

  // Buttons
  const buttonGroup = document.createElement('div');
  buttonGroup.style.cssText = 'display: flex; gap: 12px; justify-content: flex-end;';

  const cancelButton = document.createElement('button');
  cancelButton.textContent = 'Cancel';
  cancelButton.style.cssText = `
    padding: 10px 20px;
    background: #f0f0f0;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 14px;
    cursor: pointer;
    font-weight: 500;
  `;

  const exportButton = document.createElement('button');
  exportButton.textContent = 'Export';
  exportButton.style.cssText = `
    padding: 10px 20px;
    background: #6B46C1;
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    cursor: pointer;
    font-weight: 500;
  `;

  // Event handlers
  cancelButton.addEventListener('click', () => {
    container.remove();
    onComplete();
  });

  exportButton.addEventListener('click', () => {
    const selectedFormat = document.querySelector('input[name="export-format"]:checked').value;
    const customFilename = filenameInput.value || defaultFilename;

    try {
      downloadMesh(mesh, {
        format: selectedFormat,
        filename: generateFileName(customFilename, selectedFormat)
      });
    } catch (error) {
      alert(`Export failed: ${error.message}`);
    }

    container.remove();
    onComplete();
  });

  buttonGroup.appendChild(cancelButton);
  buttonGroup.appendChild(exportButton);

  dialog.appendChild(title);
  dialog.appendChild(description);
  dialog.appendChild(filenameLabel);
  dialog.appendChild(filenameInput);
  dialog.appendChild(formatLabel);
  dialog.appendChild(formatGroup);
  dialog.appendChild(buttonGroup);

  container.appendChild(dialog);

  return container;
}

/**
 * Quick export with default settings
 * @param {THREE.Mesh} mesh - The mesh to export
 * @param {string} format - Format ('stl' or 'ply')
 */
export function quickExport(mesh, format = EXPORT_CONFIG.defaultFormat) {
  try {
    downloadMesh(mesh, {
      format,
      filename: generateFileName('socket', format)
    });
  } catch (error) {
    console.error('Quick export failed:', error);
    alert(`Export failed: ${error.message}`);
  }
}
